import clsx from 'clsx';
import React, { useState } from 'react';
import Button from '../../components/Button';
import ChevronLeftIcon from '../../assets/icons/chevron-down-icon.svg?react';
import CrossIcon from '../../assets/icons/sidebar-cross-icon.svg?react';
import { GetIncidentByIdResponse } from '@EHSNavigator/backend';
import { Link } from 'react-router';
import { useApiClients } from '../../client/api/useApiClient';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import moment from 'moment';
import ModalComponent from '../../components/ModalComponent';
import { useDispatch, useSelector } from 'react-redux';
import { toggleModal } from '../../reducers/modalToggle';
import WarningIcon from '../../assets/icons/warning-icon.svg?react';
import { useButtonSizeLarge } from '../../utils/buttonSizes';

interface IncidentReportsDraftsProps {
  otherClasses?: string;
  draftIncidents: GetIncidentByIdResponse['incident'][];
}

const IncidentReportsDrafts: React.FC<IncidentReportsDraftsProps> = ({
  otherClasses,
  draftIncidents,
}) => {
  const incidentReportsDraftsClasses = clsx(
    otherClasses,
    'w-full flex-1 pt-14 px-15 md:px-25 lg:pr-14 pb-10 lg:pb-20 lg:pl-8',
  );
  const { IncidentReportingApi } = useApiClients();
  const [draftId, setDraftId] = useState('');
  const lg = useButtonSizeLarge();
  const queryClient = useQueryClient();
  const modalName = useSelector(
    (state: { modalToggle: { modalName: string } }) =>
      state.modalToggle.modalName,
  );
  const dispatch = useDispatch();
  const withdrawIncident = useMutation({
    mutationFn: async (id: string) => {
      return await IncidentReportingApi.incidentReportingControllerWithdrawIncident(
        { id },
      );
    },
    onSuccess: (res, id) => {
      dispatch(toggleModal(''));
      setDraftId('');
      toast?.success(res?.data?.message);
      queryClient.setQueryData(['all-drafts'], (oldData: any) => {
        return oldData.filter(
          (user: GetIncidentByIdResponse['incident']) => id !== user?.id,
        );
      });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message;
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => toast.error(msg));
      } else {
        toast.error(errorMessage || 'An unexpected error occurred.');
      }
    },
  });

  return (
    <div
      className={incidentReportsDraftsClasses}
      data-testid="incident-reports-drafts"
    >
      <div className="max-w-818">
        <div className="flex flex-col gap-15">
          <Button
            href="/report-incident"
            variant="link"
            otherClasses="gap-2.5 w-fit"
          >
            <span className="bg-brand-byzantine rounded-5 flex h-8 w-8 items-center justify-center">
              <ChevronLeftIcon className="h-7 w-7 rotate-90 [&>path]:fill-white" />
            </span>
            <strong>Return</strong>
          </Button>
          <h1 className="h3">Incident Reports Drafts</h1>
          <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button text-lg leading-5 font-medium lg:text-xl lg:leading-6">
            Incomplete incident reports which you can continue filling off right
            where you left them.
          </p>
        </div>
        <div className="bg-card-backgrounds/5 rounded-10 border-neutral-tones-text/10 mt-9 w-full border px-25 py-15">
          <h5 className="font-inter-variable text-neutral-tones-text -tracking-button text-lg leading-6 font-bold">
            All Drafts
          </h5>
          <div className="mt-15 flex flex-col">
            {draftIncidents?.length > 0 ? (
              <>
                {draftIncidents?.map((draft, index) => (
                  <div
                    key={index}
                    className="font-inter-variable text-neutral-tones-text -tracking-text-area border-b-neutral-tones-text/10 first:border-t-neutral-tones-text/10 flex w-full justify-between gap-8 border-b py-15 text-lg leading-5 font-normal first:border-t last:border-b-0"
                  >
                    <Link
                      className="w-full"
                      to={'/report-incident/report?draft-id=' + draft.id}
                    >
                      <p className="flex lg:justify-between lg:gap-12">
                        <span className="line-clamp-2 flex-1 grow capitalize">
                          {draft.title}
                        </span>
                        {draft.updatedAt && (
                          <span className="whitespace-nowrap">
                            {moment(draft.updatedAt).format('DD-MM-YYYY')}
                          </span>
                        )}
                      </p>
                    </Link>

                    <button
                      disabled={withdrawIncident.isPending}
                      className="group flex cursor-pointer items-baseline disabled:cursor-not-allowed"
                      onClick={() => {
                        setDraftId(draft?.id);
                        dispatch(toggleModal('remove-draft'));
                      }}
                    >
                      <CrossIcon className="[&>g>path]:fill-neutral-tones-text/50 group-hover:[&>g>path]:fill-brand-posh-pink h-5 w-5" />
                    </button>
                  </div>
                ))}
              </>
            ) : (
              <div className="my-4 flex min-h-120 flex-col items-center justify-center gap-4 text-center">
                <p className="max-w-lg text-2xl">No draft items found.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      {modalName === 'remove-draft' && (
        <ModalComponent
          customClose={() => {
            dispatch(toggleModal(''));
          }}
          otherClasses="px-4 sm:px-0 "
          additionalClass="w-full max-w-573 rounded-2xl p-5 lg:px-9 lg:py-6 "
        >
          <div className="flex flex-col items-center gap-2.5 text-center">
            <WarningIcon className="h-12 w-12 md:h-85 md:w-85" />
            <h4 className="text-brand-posh-pink">Delete Draft?</h4>
            <p className="font-inter-variable text-brand-posh-pink text-lg leading-5 font-normal">
              Are you sure you want to delete this draft? This action cannot be
              undone.
            </p>
          </div>
          <div className="mt-6 flex flex-col-reverse gap-2.5 sm:grid sm:grid-cols-2 sm:gap-6">
            <Button
              otherClasses="bg-white !text-brand-posh-pink hover:text-brand-posh-pink hover:border-brand-posh-pink "
              size={lg}
              onClick={() => dispatch(toggleModal(''))}
            >
              Cancel
            </Button>
            <Button
              otherClasses="bg-brand-posh-pink hover:text-brand-posh-pink hover:bg-transparent hover:border-brand-posh-pink"
              size={lg}
              loading={withdrawIncident?.isPending}
              disable={withdrawIncident?.isPending}
              onClick={() => withdrawIncident.mutate(draftId)}
            >
              Confirm
            </Button>
          </div>
        </ModalComponent>
      )}
    </div>
  );
};

export default IncidentReportsDrafts;
